// ── KalshiNotifyWs — WebSocket client for fill/order notifications ──
//
// One instance per WS connection. Follows HydraClient patterns:
// constructor + callbacks, start()/stop(), exponential backoff reconnect,
// heartbeat monitoring.
//
// Subscribes to 'fill' and 'order' channels for real-time account events.
// Fill parsing handles Kalshi's varied message shapes (nested payloads,
// field aliases, dollars vs cents).

import { buildHeaders, getWsUrl, type KalshiEnvironment } from '../lib/kalshi/auth';
import type { KalshiFill } from '../types/kalshi';

// ── Callbacks ───────────────────────────────────────────────────

export interface NotifyCallbacks {
  onFill: (fill: KalshiFill) => void;
  onOrderUpdate: (event: unknown) => void;
  onStatus: (connected: boolean) => void;
}

// ── Constants ───────────────────────────────────────────────────

const PING_INTERVAL = 15_000;         // 15s heartbeat check
const HEARTBEAT_TIMEOUT = 45_000;     // 3 missed pings → reconnect
const BASE_RECONNECT_DELAY = 1_000;
const MAX_RECONNECT_DELAY = 30_000;

// ── Fill Parsing ────────────────────────────────────────────────

function pickStr(...candidates: unknown[]): string | undefined {
  for (const c of candidates) {
    if (typeof c === 'string' && c !== '') return c;
  }
}

function pickNum(...candidates: unknown[]): number | undefined {
  for (const c of candidates) {
    if (typeof c === 'number' && !isNaN(c)) return c;
    if (typeof c === 'string' && c !== '') {
      const n = parseFloat(c);
      if (!isNaN(n)) return n;
    }
  }
}

function dollarsToCents(dollars: unknown, fallback?: unknown): number {
  if (typeof dollars === 'string' && dollars !== '') {
    const n = parseFloat(dollars);
    if (!isNaN(n)) return Math.round(n * 100);
  }
  if (typeof dollars === 'number') return dollars <= 1.01 ? Math.round(dollars * 100) : Math.round(dollars);
  if (typeof fallback === 'number') return Math.round(fallback);
  return 0;
}

function tryParseFill(msg: Record<string, unknown>): KalshiFill | null {
  // Unwrap nested payload (Kalshi wraps in msg/data/payload/fill)
  const payload = (
    typeof msg.msg === 'object' && msg.msg !== null ? msg.msg :
    typeof msg.data === 'object' && msg.data !== null ? msg.data :
    typeof msg.payload === 'object' && msg.payload !== null ? msg.payload :
    typeof msg.fill === 'object' && msg.fill !== null ? msg.fill :
    msg
  ) as Record<string, unknown>;

  const ticker = pickStr(payload.market_ticker, payload.ticker, payload.symbol);
  const side = pickStr(payload.side, payload.position, payload.contract) as 'yes' | 'no' | undefined;
  const action = pickStr(payload.action, payload.direction) as 'buy' | 'sell' | undefined;
  const count = pickNum(payload.count_fp, payload.fill_count_fp, payload.count, payload.quantity, payload.size);

  const rawPrice = side === 'no'
    ? dollarsToCents(payload.no_price_dollars ?? payload.no_price, payload.yes_price_dollars ?? payload.yes_price ?? payload.price)
    : dollarsToCents(payload.yes_price_dollars ?? payload.yes_price ?? payload.price_dollars ?? payload.price);

  if (!ticker || !side || !action || count === undefined) return null;

  const fillId = pickStr(payload.fill_id, payload.trade_id, payload.id)
    ?? `fill-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  return {
    fillId,
    orderId: pickStr(payload.order_id) ?? '',
    ticker,
    side,
    action,
    count,
    price: rawPrice,
    createdAt: pickStr(payload.created_at, payload.created_time) ?? new Date().toISOString(),
  };
}

// ── Client ──────────────────────────────────────────────────────

export class KalshiNotifyWs {
  private accessKey: string;
  private cryptoKey: CryptoKey;
  private environment: KalshiEnvironment;
  private callbacks: NotifyCallbacks;

  private ws: WebSocket | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private stopped = false;
  private _connected = false;
  private lastMessageMs = 0;

  constructor(
    accessKey: string,
    cryptoKey: CryptoKey,
    environment: KalshiEnvironment,
    callbacks: NotifyCallbacks,
  ) {
    this.accessKey = accessKey;
    this.cryptoKey = cryptoKey;
    this.environment = environment;
    this.callbacks = callbacks;
  }

  get connected(): boolean { return this._connected; }

  start(): void {
    this.stopped = false;
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.clearTimers();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    if (this._connected) {
      this._connected = false;
      this.callbacks.onStatus(false);
    }
  }

  // ── Connection ─────────────────────────────────────────────

  private async connect(): Promise<void> {
    if (this.stopped) return;

    const wsUrl = getWsUrl(this.environment);
    const keyLabel = this.accessKey.slice(0, 6);
    console.log(`[KalshiWS] connecting with key ${keyLabel}...`);

    try {
      // Build auth headers to pass as query params (browser WS can't set headers)
      const headers = await buildHeaders(this.accessKey, this.cryptoKey, 'GET', '/trade-api/ws/v2');
      const params = new URLSearchParams({
        api_key: headers['KALSHI-ACCESS-KEY'],
        timestamp: headers['KALSHI-ACCESS-TIMESTAMP'],
        signature: headers['KALSHI-ACCESS-SIGNATURE'],
      });

      this.ws = new WebSocket(`${wsUrl}?${params.toString()}`);

      this.ws.onopen = () => {
        this._connected = true;
        this.reconnectAttempts = 0;
        this.lastMessageMs = Date.now();
        console.log(`[KalshiWS] connected (key ${keyLabel})`);
        this.callbacks.onStatus(true);
        this.subscribe();
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        this.lastMessageMs = Date.now();
        try {
          const msg = JSON.parse(event.data as string) as Record<string, unknown>;
          this.handleMessage(msg);
        } catch (err) {
          console.warn('[KalshiWS] parse error:', err);
        }
      };

      this.ws.onclose = (event) => {
        const wasConnected = this._connected;
        this._connected = false;
        this.stopHeartbeat();
        console.log(`[KalshiWS] disconnected (key ${keyLabel}, code=${event.code})`);
        if (wasConnected) this.callbacks.onStatus(false);
        this.scheduleReconnect();
      };

      this.ws.onerror = () => {
        console.warn(`[KalshiWS] error (key ${keyLabel})`);
      };
    } catch (err) {
      console.error(`[KalshiWS] connect failed:`, err);
      this.scheduleReconnect();
    }
  }

  private subscribe(): void {
    this.send({
      id: 1,
      cmd: 'subscribe',
      params: { channels: ['fill', 'order'] },
    });
  }

  private send(msg: Record<string, unknown>): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(msg));
  }

  // ── Message Handling ───────────────────────────────────────

  private handleMessage(msg: Record<string, unknown>): void {
    const type = pickStr(msg.type, msg.channel, msg.event);

    if (type === 'fill' || type === 'order_fill' || type === 'fills') {
      const fill = tryParseFill(msg);
      if (fill) this.callbacks.onFill(fill);
      return;
    }

    if (type === 'order' || type === 'orders' || type === 'order_confirmation' || type === 'order_failure') {
      this.callbacks.onOrderUpdate(msg);
      return;
    }

    // Ignore: heartbeat acks, subscription confirmations, etc.
  }

  // ── Heartbeat ─────────────────────────────────────────────

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      const elapsed = Date.now() - this.lastMessageMs;
      if (elapsed > HEARTBEAT_TIMEOUT) {
        console.warn(`[KalshiWS] heartbeat timeout (${elapsed}ms), reconnecting...`);
        if (this.ws) {
          this.ws.close();
          this.ws = null;
        }
        this._connected = false;
        this.callbacks.onStatus(false);
        this.scheduleReconnect();
      }
    }, PING_INTERVAL);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) { clearInterval(this.heartbeatTimer); this.heartbeatTimer = null; }
  }

  // ── Reconnect ─────────────────────────────────────────────

  private scheduleReconnect(): void {
    if (this.stopped) return;
    this.clearTimers();
    const delay = Math.min(BASE_RECONNECT_DELAY * 2 ** this.reconnectAttempts, MAX_RECONNECT_DELAY);
    this.reconnectAttempts++;
    console.log(`[KalshiWS] reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  private clearTimers(): void {
    this.stopHeartbeat();
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
  }
}
